import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../store/AuthContext";
import { getVideos } from "../api/videosApi";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Alert,
  IconButton,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PlayArrow,
  Edit,
  Delete,
  Add,
  Dashboard as DashboardIcon,
  VideoLibrary,
  AccessTime,
  CalendarToday
} from "@mui/icons-material";

export default function TeacherDashboard() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadVideos();
  }, []);

  // Function to extract YouTube video ID and get thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    
    if (match && match[1]) {
      const videoId = match[1];
      // Try maxresdefault first, fallback to hqdefault if not available
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    
    return null;
  };

  // Get thumbnail height based on device
  const getThumbnailHeight = () => {
    if (isMobile) return 180;
    if (isTablet) return 200;
    return 200; // Desktop
  };

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getVideos(token, { teacher_id: user.id });     
      setVideos(res.data);
    } catch (err) {
      console.error("Error loading videos", err);
      setError("Failed to load videos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      await axios.delete(`https://lms.childinfo.in/api/videos.php?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert("Video deleted successfully!");
      loadVideos();
    } catch (err) {
      console.error(err);
      alert("Delete failed. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      py: { xs: 1, sm: 2, md: 3 }, // Reduced padding
      px: { xs: 0.5, sm: 2, md: 3 }, // Reduced horizontal padding on mobile
      margin: 0,
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2, md: 3, lg: 4 }, // Reduced padding on mobile
          mb: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 0, sm: 0 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: { xs: 2, sm: 3 }, // Smaller border radius on mobile
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          mx: { xs: 0.5, sm: 0 }, // Minimal horizontal margin on mobile
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          alignItems: { xs: "flex-start", sm: "center" }, 
          justifyContent: "space-between", 
          gap: { xs: 1.5, sm: 2 } 
        }}>
          <Box sx={{ width: '100%' }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} // Smaller heading on mobile
              component="h1" 
              sx={{ 
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              <DashboardIcon sx={{ fontSize: { xs: 24, sm: 32, md: 40 } }} />
              Welcome, {user?.name}!
            </Typography>
            <Typography variant={isMobile ? "body2" : "h6"} sx={{ opacity: 0.9 }}>
              Manage your educational videos and content
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            size={isMobile ? "small" : "medium"} // Smaller button on mobile
            startIcon={<Add />}
            onClick={() => navigate("/teacher/upload-video")}
            sx={{
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              "&:hover": {
                background: "rgba(255,255,255,0.3)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              },
              transition: "all 0.2s ease",
              minWidth: { xs: "100%", sm: "auto" },
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }
            }}
          >
            Upload New Video
          </Button>
        </Box>
      </Paper>

      {/* Stats Section - Simplify for mobile */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              textAlign: "center",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <VideoLibrary sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, mb: 1 }} />
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              {videos.length}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"}>Total Videos</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Videos Grid - Adjust for mobile */}
      {error && (
        <Alert severity="error" sx={{ 
          mb: 2, 
          borderRadius: 2,
          fontSize: { xs: '0.8rem', sm: '0.9rem' }
        }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: { xs: 4, sm: 6, md: 8 } }}>
          <CircularProgress size={isMobile ? 40 : 60} sx={{ color: theme.palette.primary.main }} />
        </Box>
      ) : videos.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            textAlign: "center",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white",
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <VideoLibrary sx={{ fontSize: { xs: 48, sm: 56, md: 64 }, mb: 2, opacity: 0.8 }} />
          <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
            No Videos Yet
          </Typography>
          <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 3, opacity: 0.9 }}>
            Start by uploading your first educational video
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? "small" : "medium"}
            startIcon={<Add />}
            onClick={() => navigate("/teacher/upload-video")}
            sx={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              "&:hover": {
                background: "rgba(255,255,255,0.3)",
              }
            }}
          >
            Upload First Video
          </Button>
        </Paper>
      ) : (
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
            mb: { xs: 2, sm: 3 }, 
            fontWeight: "bold", 
            display: "flex", 
            alignItems: "center", 
            gap: 1,
            px: { xs: 0.5, sm: 0 } // Minimal horizontal padding on mobile
          }}>
            <VideoLibrary />
            Your Videos ({videos.length})
          </Typography>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {videos.map((video) => {
              const thumbnailUrl = getYouTubeThumbnail(video.file_path);
              const thumbnailHeight = getThumbnailHeight();
              
              return (
                <Grid item xs={12} key={video.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "row", // Horizontal layout for mobile
                      flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on larger
                      transition: "all 0.2s ease",
                      border: "1px solid rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      "&:hover": {
                        transform: { xs: "none", sm: "translateY(-4px)" }, // No transform on mobile
                        boxShadow: { xs: "none", sm: "0 8px 25px rgba(0,0,0,0.12)" },
                      },
                      mx: { xs: 0, sm: 'auto' }, // No horizontal margin on mobile
                    }}
                  >
                    {/* Thumbnail */}
                    <CardActionArea 
                      onClick={() => window.open(video.file_path, "_blank")}
                      sx={{ 
                        width: { xs: '100%', sm: 250, md: 360 },
                        flexShrink: 0
                      }}
                    >
                      <CardMedia
                        sx={{
                          height: { xs: 180, sm: 200, md: 240 },
                          position: 'relative',
                          background: thumbnailUrl 
                            ? `url(${thumbnailUrl}) center/cover no-repeat`
                            : `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                        }}
                      >
                        {/* Play Button Overlay */}
                        <Box
                          className="play-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: { xs: 1, sm: 0 }, // Always visible on mobile
                            transition: 'opacity 0.2s ease',
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 40, sm: 50, md: 60 },
                              height: { xs: 40, sm: 50, md: 60 },
                              borderRadius: '50%',
                              background: 'rgba(255,0,0,0.8)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              border: '2px solid white',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                            }}
                          >
                            <PlayArrow sx={{ 
                              fontSize: { xs: 24, sm: 28, md: 36 },
                              ml: 0.5
                            }} />
                          </Box>
                        </Box>
                        
                        {/* Duration Badge */}
                        {video.duration && (
                          <Chip
                            icon={<AccessTime />}
                            label={formatDuration(video.duration)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              background: 'rgba(0,0,0,0.8)',
                              color: 'white',
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: 22,
                              fontWeight: 'bold',
                              '& .MuiChip-icon': {
                                fontSize: { xs: 14, sm: 16 }
                              }
                            }}
                          />
                        )}
                      </CardMedia>
                    </CardActionArea>

                    {/* Content */}
                    <CardContent sx={{ 
                      flex: 1, 
                      p: { xs: 1.5, sm: 2, md: 3 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <Box>
                        {/* Video Title */}
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          component="h2"
                          sx={{
                            fontWeight: "bold",
                            mb: { xs: 1, sm: 1.5 },
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.3,
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                          }}
                          onClick={() => window.open(video.file_path, "_blank")}
                        >
                          {video.title}
                        </Typography>

                        {/* Video Info Chips */}
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}>
                          <Chip
                            label={`Class: ${video.class_name}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                          />
                          <Chip
                            label={`Subject: ${video.subject_name}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                          />
                        </Stack>

                        {/* Date */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 0.5,
                            fontSize: { xs: '0.75rem', sm: '0.85rem' }
                          }}
                        >
                          <CalendarToday sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          {formatDate(video.created_at)}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Stack 
                        direction="row" 
                        spacing={1}
                        sx={{ 
                          mt: 2,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          gap: { xs: 0.5, sm: 1 }
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => window.open(video.file_path, "_blank")}
                          sx={{
                            background: "#FF0000",
                            fontWeight: "bold",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            flex: { xs: '1 0 auto', sm: 'none' }
                          }}
                        >
                          {isMobile ? 'Watch' : 'Watch on YouTube'}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => navigate(`/teacher/edit-video/${video.id}`)}
                          sx={{ 
                            fontWeight: "bold",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            flex: { xs: '1 0 auto', sm: 'none' }
                          }}
                        >
                          {isMobile ? 'Edit' : 'Edit'}
                        </Button>
                        
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => deleteVideo(video.id, video.title)}
                          sx={{ 
                            fontWeight: "bold",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            flex: { xs: '1 0 auto', sm: 'none' }
                          }}
                        >
                          {isMobile ? 'Delete' : 'Delete'}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
}